import {
  Button,
  Table,
  Row,
  Col,
  Spinner,
  Alert,
  Modal,
  Form,
  Badge,
} from "react-bootstrap";
import { useState } from "react";
import PageFade from "../../components/animation/PageFade";
import {
  useGetColorsQuery,
  useCreateColorMutation,
  useUpdateColorMutation,
  useDeleteColorMutation,
} from "../../services/api";
import { toast } from "react-toastify";
import { Color } from "../../types";

export default function Colors() {
  const { data, isLoading, error, refetch } = useGetColorsQuery();
  const [createColor, { isLoading: isCreating }] = useCreateColorMutation();
  const [updateColor, { isLoading: isUpdating }] = useUpdateColorMutation();
  const [deleteColor, { isLoading: isDeleting }] = useDeleteColorMutation();

  const [showModal, setShowModal] = useState(false);
  const [editingColor, setEditingColor] = useState<Color | null>(null);
  const [formData, setFormData] = useState({ name: "", hexCode: "#000000" });

  const colors = data?.data || [];

  const handleOpenModal = (color?: Color) => {
    if (color) {
      setEditingColor(color);
      setFormData({
        name: color.name || "",
        hexCode: color.hexCode || "#000000",
      });
    } else {
      setEditingColor(null);
      setFormData({ name: "", hexCode: "#000000" });
    }
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingColor(null);
    setFormData({ name: "", hexCode: "#000000" });
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.name.trim()) {
      toast.error("Color name is required");
      return;
    }
    if (!formData.hexCode.trim()) {
      toast.error("Hex code is required");
      return;
    }

    try {
      if (editingColor) {
        // According to guide: Use application/json for Colors
        await updateColor({ id: editingColor._id, name: formData.name, hexCode: formData.hexCode }).unwrap();
        toast.success("Color updated successfully");
      } else {
        // According to guide: Use application/json for Colors
        await createColor(formData).unwrap();
        toast.success("Color created successfully");
      }
      handleCloseModal();
    } catch (error: any) {
      console.error('Color operation failed:', error);
      toast.error(
        error?.data?.message ||
          `Failed to ${editingColor ? "update" : "create"} color`
      );
    }
  };

  const handleDelete = async (id: string, name: string) => {
    if (window.confirm(`Are you sure you want to delete "${name}"?`)) {
      try {
        await deleteColor(id).unwrap();
        toast.success("Color deleted successfully");
      } catch (error: any) {
        toast.error(error?.data?.message || "Failed to delete color");
      }
    }
  };

  if (isLoading) {
    return (
      <PageFade>
        <div className="text-center py-5">
          <Spinner animation="border" role="status">
            <span className="visually-hidden">Loading...</span>
          </Spinner>
        </div>
      </PageFade>
    );
  }

  if (error) {
    const errorMessage =
      (error as any)?.data?.message ||
      (error as any)?.error ||
      "Failed to load colors. Please check your connection and try again.";
    const status = (error as any)?.status;

    return (
      <PageFade>
        <Alert variant="danger">
          <Alert.Heading>Error Loading Colors</Alert.Heading>
          <p>{errorMessage}</p>
          {status === 401 && (
            <p className="mb-0">
              <small>Your session may have expired. Please log in again.</small>
            </p>
          )}
          {status === 403 && (
            <p className="mb-0">
              <small>You don't have permission to access this resource.</small>
            </p>
          )}
          <hr />
          <div className="d-flex justify-content-end">
            <Button
              onClick={() => refetch()}
              variant="outline-danger"
              size="sm"
            >
              Retry
            </Button>
          </div>
        </Alert>
      </PageFade>
    );
  }

  return (
    <PageFade>
      <Row className="mb-3">
        <Col>
          <h3>Colors</h3>
        </Col>
        <Col className="text-end">
          <Button onClick={() => handleOpenModal()}>Add Color</Button>
        </Col>
      </Row>
      {colors.length === 0 ? (
        <Alert variant="info">
          No colors found. Click "Add Color" to create your first color.
        </Alert>
      ) : (
        <Table hover responsive>
          <thead>
            <tr>
              <th>Color</th>
              <th>Name</th>
              <th>Hex Code</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {colors.map((color) => (
              <tr key={color._id} style={{ backgroundColor: "#fff" }}>
                <td>
                  <div
                    style={{
                      width: "40px",
                      height: "40px",
                      backgroundColor: color.hexCode,
                      borderRadius: "4px",
                      border: "1px solid #ddd",
                    }}
                  />
                </td>
                <td>{color.name}</td>
                <td>
                  <Badge bg="secondary">{color.hexCode.toUpperCase()}</Badge>
                </td>
                <td className="text-end">
                  <Button
                    size="sm"
                    variant="outline-primary"
                    className="me-2"
                    onClick={() => handleOpenModal(color)}
                  >
                    Edit
                  </Button>
                  <Button
                    size="sm"
                    variant="outline-danger"
                    onClick={() => handleDelete(color._id, color.name)}
                  >
                    Delete
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      )}

      <Modal show={showModal} onHide={handleCloseModal}>
        <Modal.Header closeButton>
          <Modal.Title>{editingColor ? "Edit Color" : "Add Color"}</Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleSubmit}>
          <Modal.Body>
            <Row className="g-3">
              <Col md={12}>
                <Form.Group>
                  <Form.Label>Name *</Form.Label>
                  <Form.Control
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    placeholder="Color name"
                    required
                  />
                </Form.Group>
              </Col>
              <Col md={12}>
                <Form.Group>
                  <Form.Label>Hex Code *</Form.Label>
                  <div className="d-flex gap-3 align-items-center">
                    <Form.Control
                      type="color"
                      name="hexCode"
                      value={formData.hexCode}
                      onChange={handleInputChange}
                      style={{ width: "60px", height: "40px" }}
                    />
                    <Form.Control
                      type="text"
                      name="hexCode"
                      value={formData.hexCode}
                      onChange={handleInputChange}
                      placeholder="#000000"
                      pattern="^#([A-Fa-f0-9]{6}|[A-Fa-f0-9]{3})$"
                      required
                    />
                  </div>
                  <Form.Text className="text-muted">
                    Select or enter a hex color code
                  </Form.Text>
                </Form.Group>
              </Col>
            </Row>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={handleCloseModal}>
              Cancel
            </Button>
            <Button type="submit" disabled={isCreating || isUpdating}>
              {isCreating || isUpdating ? (
                <>
                  <Spinner animation="border" size="sm" className="me-2" />
                  {editingColor ? "Updating..." : "Creating..."}
                </>
              ) : editingColor ? (
                "Update Color"
              ) : (
                "Create Color"
              )}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>
    </PageFade>
  );
}
